// src/features/release-notes/session.ts
//
// The prompt's decisions, apart from React: when to ask, what a failure means, what acknowledges.

import type { ManualLang } from '@/features/manual/types';
import { getImpersonation } from '@/utils/impersonation';
import { isDocumentationOnly } from '@/features/developers/access';
import { releaseNotesApi } from './api';
import type { ReleaseNote } from './types';

type Api = Pick<typeof releaseNotesApi, 'pending' | 'markSeen'>;

let askedThisSession = false;

/** Test seam: forgets that the session was already asked. */
export const resetReleaseNotesSession = (): void => {
  askedThisSession = false;
};

/**
 * The notes to show at sign-in: asked once per session (a module flag, so remounting the shell does
 * not ask again), never while staff act as someone else (they must not acknowledge for that person),
 * and empty on any failure — an older API answers 404 and the app simply shows nothing.
 */
export const loadPendingNotes = async (lang: ManualLang, api: Api = releaseNotesApi): Promise<ReleaseNote[]> => {
  if (askedThisSession || getImpersonation() || isDocumentationOnly()) return [];
  askedThisSession = true;
  try {
    const pending = await api.pending(lang);
    return Array.isArray(pending) ? pending : [];
  } catch {
    return [];
  }
};

/** «Entendido», or following a note: acknowledges everything shown. A failure is not shown. */
export const acknowledgeNotes = async (notes: ReleaseNote[], api: Api = releaseNotesApi): Promise<void> => {
  if (notes.length === 0) return;
  try {
    await api.markSeen(notes.map((note) => note.id));
  } catch {
    // The dialog is already closed; the notes come back at the next session.
  }
};

/**
 * What the dialog's two exits do. Both close it; only `acknowledge` («Entendido», or following a
 * note) tells the server — the cross, Escape and the backdrop leave the notes pending.
 */
export const promptHandlers = (notes: ReleaseNote[], close: () => void, api: Api = releaseNotesApi) => ({
  acknowledge: () => {
    close();
    void acknowledgeNotes(notes, api);
  },
  dismiss: () => close(),
});
